/* eslint-disable react/jsx-no-comment-textnodes */

import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// @ts-ignore legacy boundary
import { LaunchRounded, StorefrontRounded } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as backend from 'src/backend';
import { getS3Url } from 'src/constants';
import { parseJsonArray } from 'src/utils/json';
import {
  MARKETPLACE_CHIP_SX,
  MARKETPLACE_FOOD_TYPE_ALIASES,
  MARKETPLACE_FOOD_TYPE_IDS,
  formatCurrencyFromCents,
} from 'src/utils/marketplace';
import { useFoodTypesSelector } from 'src/components/DonationFoodTypesSelector';
import { PATH_DASHBOARD } from 'src/routes/paths';

export interface MarketplaceChatListingHeaderProps {
  listingId?: string | number;
  fallbackTitle?: string;
  showSellerName?: boolean;
  fallbackSellerName?: string;
  [key: string]: any;
}

// Module-level cache so each chat surface (listing drawer, marketplace inbox,
// main chat popover) fetches a listing at most once per session. Failed
// lookups (e.g. 404s for listings the viewer can no longer access) cache as
// null, which renders the friendlyName-only fallback.
const listingPromiseCache = new Map();

function loadListing(listingId) {
  if (!listingPromiseCache.has(listingId)) {
    listingPromiseCache.set(
      listingId,
      backend.marketplace.getListingById(listingId).catch(() => null)
    );
  }
  return listingPromiseCache.get(listingId);
}

/**
 * The shared rich header for marketplace listing conversations: listing
 * thumbnail (photo or category-icon fallback), title, price summary, a status
 * chip when the listing is no longer purchasable, and a "View listing" link
 * when it still is. Used by the listing-page chat drawer, the Marketplace >
 * Messages inbox thread, and the main chat popover (seller side).
 *
 * Falls back gracefully to the conversation's friendly name (and optional
 * seller name) while loading or when the listing can't be fetched.
 */
export default function MarketplaceChatListingHeader({
  listingId,
  fallbackTitle = '',
  showSellerName = false,
  fallbackSellerName = '',
}: MarketplaceChatListingHeaderProps) {
  const { t } = useTranslation('translation');
  const foodTypeOptions = useFoodTypesSelector();
  const [listing, setListing] = useState(null);

  useEffect(() => {
    setListing(null);
    if (!listingId) {
      return undefined;
    }
    let cancelled = false;
    loadListing(listingId).then((data) => {
      if (!cancelled) {
        setListing(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const title = listing?.title || fallbackTitle;
  const sellerName = showSellerName
    ? listing?.donor_business_name || fallbackSellerName
    : '';

  // Thumbnail: first listing photo, else the category icon for the first
  // recognized food type (same normalization as ListingDetails), else the
  // generic storefront icon.
  const firstImage = parseJsonArray(listing?.images).filter(Boolean)[0];
  const imageUrl = firstImage ? getS3Url(firstImage) : null;
  const firstFoodType = parseJsonArray(listing?.food_types).filter(Boolean)[0];
  const normalizedFoodTypeId =
    typeof firstFoodType === 'string' && firstFoodType.trim()
      ? MARKETPLACE_FOOD_TYPE_ALIASES[firstFoodType.trim().toUpperCase()] ||
        firstFoodType.trim().toLowerCase()
      : null;
  const FallbackIcon =
    (MARKETPLACE_FOOD_TYPE_IDS.has(normalizedFoodTypeId) &&
      foodTypeOptions.find((option) => option.id === normalizedFoodTypeId)
        ?.icon) ||
    StorefrontRounded;

  // Price summary in the same "$15.00 / case" format as the listing pages.
  let priceLine = null;
  if (listing) {
    const isPerUnit = ['per_item', 'per_weight'].includes(listing.pricing_type);
    const amount = formatCurrencyFromCents(
      isPerUnit ? listing.price_per_unit_cents : listing.total_price_cents
    );
    if (isPerUnit) {
      const unit =
        listing.pricing_type === 'per_item'
          ? listing.unit_label || t('marketplace.units.items')
          : t(`marketplace.units.${listing.weight_unit || 'lb'}`, {
              defaultValue: listing.weight_unit || 'lb',
            });
      priceLine = `${amount} / ${unit}`;
    } else {
      priceLine = amount;
    }
  }

  const isPurchasable =
    !!listing &&
    !listing.is_deleted &&
    ['active', 'partially_sold'].includes(listing.status);
  const isSoldOut = listing?.status === 'sold';
  const statusChipLabel = !listing
    ? null
    : isSoldOut
      ? t('marketplace.listing-details.sold-out', { defaultValue: 'Sold out' })
      : !isPurchasable
        ? t('marketplace.listing-chat.no-longer-available', {
            defaultValue: 'No longer available',
          })
        : null;
  const viewListingLabel = t('marketplace.listing-chat.view-listing', {
    defaultValue: 'View listing',
  });

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ minWidth: 0, flexGrow: 1 }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <FallbackIcon
            sx={{
              fontSize: 24,
              color: (theme) => alpha(theme.palette.primary.main, 0.48),
            }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" noWrap>
          {title}
        </Typography>
        {sellerName ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            component="p"
            sx={{ m: 0 }}
          >
            {sellerName}
          </Typography>
        ) : null}
        {(priceLine || statusChipLabel) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            {priceLine && (
              <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
                {priceLine}
              </Typography>
            )}
            {statusChipLabel && (
              <Chip
                size="small"
                label={statusChipLabel}
                sx={{
                  ...MARKETPLACE_CHIP_SX,
                  height: 18,
                  fontSize: 11,
                  fontWeight: 700,
                  ...(isSoldOut && {
                    bgcolor: 'error.lighter',
                    color: 'error.darker',
                  }),
                }}
              />
            )}
          </Stack>
        )}
      </Box>

      {isPurchasable && (
        <Tooltip title={viewListingLabel}>
          <IconButton
            size="small"
            component={Link}
            to={PATH_DASHBOARD.marketplace.viewListing(listingId)}
            aria-label={viewListingLabel}
            sx={{ flexShrink: 0 }}
          >
            <LaunchRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
